import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CATEGORIES_ENUMS } from "constants/navigation/categories";
import {
  GameType,
  GAME_SORT_ENUMS,
  ProviderType,
  NewHistoryResponseType,
} from "types/Games";

export type CategoryType = {
  id: CATEGORIES_ENUMS;
  icon: JSX.Element | null;
  image?: string;
  title: string;
  url: string;
};

export type FilterType = {
  providers: string[] | null;
  take: number;
  page: number;
  top?: boolean | null;
  sort: string;
};

export type GameState = {
  games: GameType[] | null;
  searchResult: GameType[] | null;
  providers: ProviderType[] | null;
  searchGames: string;
  filter: FilterType;
  category: CATEGORIES_ENUMS | null;
  isFilterReseted: boolean;
  lastPage: number;
  gameName: string;
  gameUrl: string;
  favorite: boolean;
  currentGame: GameType | null;
  currentAliasGame: string;
  history: GameType[] | null;
  newHistory: NewHistoryResponseType[] | null;
};

const initialState: GameState = {
  games: null,
  searchResult: null,
  providers: null,
  filter: {
    providers: [],
    take: 15,
    page: 1,
    top: true,
    sort: GAME_SORT_ENUMS.POP,
  },
  searchGames: "",
  isFilterReseted: false,
  category: CATEGORIES_ENUMS.POPULAR,
  lastPage: 0,
  gameUrl: "",
  gameName: "",
  favorite: false,
  currentGame: null,
  currentAliasGame: "",
  history: null,
  newHistory: null,
};

export const gameSlice = createSlice({
  name: "game",
  initialState,
  reducers: {
    setGames(state, action: PayloadAction<GameType[]>) {
      state.games = action.payload;
    },
    setMoreGames(state, action: PayloadAction<GameType[]>) {
      const games = state.games || [];
      state.games = [...games, ...action.payload];
    },
    setProviders(state, action: PayloadAction<ProviderType[]>) {
      state.isFilterReseted = false;
      state.providers = action.payload;
      state.filter.page = 1;
    },
    setSort(state, action: PayloadAction<string>) {
      state.isFilterReseted = false;
      state.filter.sort = action.payload;
      state.filter.page = 1;
    },
    setTake(state, action: PayloadAction<number>) {
      state.filter.take = action.payload;
      state.filter.page = 1;
    },
    setPage(state, action: PayloadAction<number>) {
      state.isFilterReseted = false;
      state.filter.page = action.payload;
    },
    setProviderFilter(state, action: PayloadAction<string | null>) {
      const value = action.payload;
      if (value) {
        const { providers } = state.filter;
        if (providers?.includes(value)) {
          state.filter.providers = providers.filter(
            (p) => p !== action.payload,
          );
        } else {
          providers?.push(value);
          state.filter.providers = providers;
        }
      } else {
        state.filter.providers = [];
      }
      state.filter.page = 1;
    },
    setProviderPage(state, action: PayloadAction<string | null>) {
      const value = action.payload;
      state.isFilterReseted = false;
      if (value) {
        state.filter.providers = value.split(",");
      } else {
        state.filter.providers = [];
      }
      state.filter.page = 1;
    },
    setCategory(state, action: PayloadAction<CATEGORIES_ENUMS | null>) {
      state.category = action.payload;
      state.isFilterReseted = false;
      state.filter.sort = GAME_SORT_ENUMS.POP;
      state.filter.page = 1;
    },
    setLastPage(state, action: PayloadAction<number>) {
      state.isFilterReseted = false;
      state.lastPage = action.payload;
    },
    setGameName(state, action: PayloadAction<string>) {
      state.gameName = action.payload;
    },
    setGameUrl(state, action: PayloadAction<string>) {
      state.gameUrl = action.payload;
    },
    searchGamesChange(state, action: PayloadAction<string>) {
      state.searchGames = action.payload;
      state.filter.page = 1;
    },
    setSearchResult(state, action: PayloadAction<GameType[]>) {
      state.searchResult = action.payload;
    },
    setSearchResultMore(state, action: PayloadAction<GameType[]>) {
      const searchResult = state.searchResult || [];
      state.searchResult = [...searchResult, ...action.payload];
    },
    setCurrentGame(state, action: PayloadAction<GameType | null>) {
      state.currentGame = action.payload;
    },
    setCurrentGameAlias(state, action: PayloadAction<string>) {
      state.currentAliasGame = action.payload;
    },
    setFavorite(state, action: PayloadAction<boolean>) {
      state.favorite = action.payload;
    },
    setHistory(state, action: PayloadAction<GameType[]>) {
      state.history = action.payload;
    },
    setNewHistory(state, action: PayloadAction<NewHistoryResponseType[]>) {
      state.newHistory = action.payload;
    },
    setDefaultFilters(state) {
      state.isFilterReseted = true;
      state.filter = {
        providers: [],
        take: 15,
        page: 1,
        top: true,
        sort: GAME_SORT_ENUMS.POP,
      };
      state.category = CATEGORIES_ENUMS.POPULAR;
      state.lastPage = 0;
    },
  },
});

export const {
  setGames,
  setMoreGames,
  setProviders,
  setProviderFilter,
  setProviderPage,
  setLastPage,
  setCategory,
  setSort,
  setTake,
  setPage,
  setGameName,
  setGameUrl,
  searchGamesChange,
  setCurrentGame,
  setCurrentGameAlias,
  setSearchResult,
  setSearchResultMore,
  setFavorite,
  setHistory,
  setDefaultFilters,
  setNewHistory,
} = gameSlice.actions;

export default gameSlice.reducer;
